<template>
	<AppDialog disableMaxWidth :closeRight="'-10'" :closeTop="'-30'" dialogName="SetCustomDomainForWorkflowDialog" width="620">
		<Col class="dialog-content rounded px-9 py-4 flex-justify-space-between">
			<Col>
				<Row class="title-text mb-2">Customize URL</Row>
				<h3 class="mb-4">
					Let customers know what this portal is about.<br />Remember to update links you already shared if you change this.
				</h3>
				<Row class="width-100">
					<AppSelect
						no-data-text="No custom domains"
						outlined
						dense
						class="width-100 select-domain"
						:item-text="getItemText"
						item-value="id"
						v-model="customDomain"
						:items="customDomains"
						placeholder="Select a domain"
						:menu-props="menuProps"
						min-width="360"
						width="360"
					>
						<template v-slot:append-item>
							<AnemicButton v-if="canAddDomain" @click="addNewDomain" class="add-domain-button mt-2">
								<SIcon class="mr-1 mt-1" name="add-new-accound-plus" width="13" />
								Add domain
							</AnemicButton>
						</template>

						<template v-slot:item="{ item }">
							<Row class="py-2 width-100 flex-align-center flex-justify-space-between">
								<h3 class="reg">https://{{ item.domain }}/</h3>
								<AppVMenu v-if="canAddDomain" width="200" min-width="200" bottom left offset-y content-class="dropdown-menu-container">
									<template v-slot:activator="{ on }">
										<AnemicButton
											v-on="on"
											@click="(e) => e.preventDefault()"
											width="22"
											height="22"
											icon
											fab
											x-small
											class="flex-centerize menu-button ml-12"
										>
											<SIcon name="three-dot-menu" width="13" class="menu-icon" />
										</AnemicButton>
									</template>
									<v-list>
										<v-list-item class="item-container" @click="deleteDomain(item)">
											<v-list-item-title>
												<span style="color: #3e495a; font-size: 14px; font-weight: 400"> Delete domain </span>
											</v-list-item-title>
										</v-list-item>
									</v-list>
								</AppVMenu>
							</Row>
						</template>
					</AppSelect>
					<AppTextField
						dense
						class="width-100"
						type="text"
						single-line
						v-model="customDomainURL"
						@input="validateCustomDomainURL"
						iconName="url"
						placeholder="URL Slug"
						outlined
					/>
				</Row>
				<h5 class="secondary-color bold mt-5">Your default Ply URL will still work</h5>
				<a target="_blank" :href="plyLink" class="link mt-1 secondary-color">{{ plyLink }}</a>
				<h5 class="secondary-color mt-4">
					If you’re going to embed the portal in other apps (like Notion) via automatic embed (when you paste a link to embed), then use the
					Ply URL.
				</h5>
			</Col>
			<Row v-if="showWarning" class="flex-centerize">
				<h5 class="text-red">Caution: You are about to remove custom url <br /></h5>
			</Row>
			<Row v-if="showRemoveCustomDomain" class="flex-centerize">
				<h3 @click="removeCustomDomain" class="remove-link">Remove custom URL</h3>
			</Row>
			<Row class="flex-centerize">
				<AnemicButton :disabled="!formValid" :loading="isLoading" @click="saveCustomURL" class="pa-0 action-button" tile>
					Save Custom URL
				</AnemicButton>
			</Row>
		</Col>
	</AppDialog>
</template>
<script>
import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { fetchCustomDomains, deleteCustomDomain } from '@/store/modules/customDomains/actions';
import { useCustomDomainsArray } from '@/store/modules/customDomains/getters';
import { updateWorkflow } from '@/store/modules/workflows/actions';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
export default {
	name: 'SetCustomDomainForWorkflowDialog',
	components: { AppDialog },
	props: {
		workflow: { type: Object, required: true },
		plyLink: { type: String, required: true },
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGettersShorthand({ useCustomDomainsArray, hasAccountPermission }),
		customDomains() {
			return this.useCustomDomainsArray.filter((item) => item.verified);
		},
		requiredRules() {
			if (this.showWarning) return [];
			return [(v) => !!v || 'Required'];
		},
		slugRules() {
			if (this.showWarning) return [];
			return [(v) => !!v || 'Required', (v) => /^[a-zA-Z0-9-]*$/.test(v) || 'Only letters, numbers and hyphens allowed'];
		},
		showRemoveCustomDomain() {
			return this.customDomain && this.workflow.tool_preferences.custom_domain;
		},
		formValid() {
			const customDomainURLValid = this.slugRules.every((rule) => rule(this.customDomainURL) === true);
			const customDomainValid = this.requiredRules.every((rule) => rule(this.customDomain) === true);
			return customDomainURLValid && customDomainValid;
		},
		canAddDomain() {
			return this.hasAccountPermission(AccountPermissions.ChangeAccount);
		},
		menuProps() {
			return { rounded: 'lg', offsetY: true };
		},
	},
	methods: {
		...mapActionsShorthand({ updateWorkflow, fetchCustomDomains, deleteCustomDomain }),
		async deleteDomain(domain) {
			const confirmedDelete = await AppDialogManagerService.openConfirmationDialog({
				title: `Delete custom domain ${domain.domain}?`,
				description: `Are you sure you want to delete this custom domain? this action cannot be undone.`,
				yesText: 'Yes',
				noText: 'No',
				isDanger: true,
			});
			if (confirmedDelete === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				try {
					await this.deleteCustomDomain({ domainId: domain.id });
				} catch {
					this.$toast.error('Something went wrong, please try again later');
				}
			}
		},
		removeCustomDomain() {
			this.customDomain = null;
			this.customDomainURL = null;
			this.showWarning = true;
		},
		getItemText(domainObject) {
			return `https://${domainObject.domain}/`;
		},
		addNewDomain() {
			AppDialogManagerService.openNewCustomDomainDialog();
		},
		close() {
			this.dialogPromiseHandlers.resolve();
			AppDialogManagerService.closeCurrentDialog();
		},
		async saveCustomURL() {
			this.isLoading = true;
			try {
				const data = {
					tool_preferences: { custom_domain: this.customDomain, custom_domain_url: this.customDomainURL },
				};
				await this.updateWorkflow({ id: this.workflow.id, data });
				this.$toast.success('Saved successfully');
				this.close();
			} catch (err) {
				if (err.response?.data.error?.msg) {
					this.$toast.error(`Error while saving: ${err.response?.data.error?.msg}`);
				} else {
					this.$toast.error(`Error while saving`);
					throw err;
				}
			} finally {
				this.isLoading = false;
			}
		},
		validateCustomDomainURL() {
			this.$nextTick(() => {
				this.customDomainURL = this.customDomainURL
					.toString() // Convert to string
					.toLowerCase() // Convert to lowercase
					.replace(/\s+/g, '-') // Replace spaces with hyphens
					.replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
					.replace(/[^a-z0-9 -]/g, ''); // Remove all non-URL slug allowed chars
			});
		},
	},
	watch: {},
	mounted() {
		this.fetchCustomDomains();
		this.customDomain = this.workflow.tool_preferences.custom_domain;
		this.customDomainURL = this.workflow.tool_preferences.custom_domain_url;
	},
	data() {
		return {
			showWarning: false,
			customDomain: null,
			customDomainURL: '',
			isLoading: false,
		};
	},
};
</script>

<style scoped lang="scss">
.dropdown-menu-container {
	width: 300px;
	overflow: hidden;
	border-radius: 11px;
	background: var(--bg-color);
	box-shadow: 0px 3px 16px #0000004d;
}
.select-domain {
	min-width: 320px;
}
.dialog-content {
	width: 620px;
	height: 350px;
	overflow: hidden;
	background: var(--white);
}

.rounded {
	border-radius: 10px;
}

.action-button {
	width: 132px !important;
	height: 40px !important;
	font-size: 14px !important;
	border-radius: 8px !important;
	letter-spacing: 0px !important;
	color: var(--white) !important;
	font-weight: normal !important;
	text-transform: none !important;
	background: #5858ec !important;
}
.title-text {
	font-size: 22px;
	font-weight: bold;
	color: var(--primary-font-color);
}
.subtext {
	color: var(--secondary-font-color);
	font-size: 14px;
}
.secondary-color {
	color: #7c7c7c;
}
.link {
	color: #7c7c7c;
	font-size: 12px;
	width: fit-content;
}
.v-list-item__content {
	font-size: 12px;
}
.text-red {
	color: red;
}
.add-domain-button {
	width: 100% !important;
	height: 44px !important;
	padding: 0px !important;
	border-radius: 0px 0px 8px 8px !important;
	background: var(--ask-for-ingreation-background) !important;

	font-size: 14px !important;
	text-transform: none !important;
	color: var(--publish-button-background) !important;

	::v-deep {
		.v-btn__content {
			border-radius: 0px 0px 8px 8px !important;
		}
	}
}
.menu-button {
	background: white !important;
	&:hover {
		border: 1px solid var(--forth-border-color);
	}
	.menu-icon {
		margin-top: -6px;
		margin-left: -2px;
	}
}
.item-container {
	max-height: 30px !important;
	min-height: 30px !important;
}
.remove-link {
	cursor: pointer;
	text-decoration: underline;
}
</style>
